import React, { useEffect, useRef } from "react";
import useChartEventHandler from "@ui/Highcharts/hooks/useChartEventHandler";
import useSelectedAnalyses, { closestSelectableAnalysis } from "../../Analysis/useSelectedAnalyses";
import type { Analysis } from "../../../../api/Analysis";
import Highcharts from "highcharts";
import { MIN_DELTA } from "@ui/Highcharts/plugins/betterZoom";

/**
 * @description
 * Firefox drag event still trigger click event.
 * It seems to be a spec that is not exactly implemented by Chromium browser.
 * From the spec, a click is trigger when a mouseup event is triggered on the same
 * element as the mousedown event and no mouseenter / leave event is triggered between the two.
 * It is not related to the distance (space or time) between the mousedown and mouseup event.
 *
 * The workaround is to detect if the user is dragging the mouse and prevent the click event if it's the case.
 *
 * @note It currently works as Highcharts click event are triggered before native addEventListener click event.
 *
 * @see {https://bugzilla.mozilla.org/show_bug.cgi?id=319347}
 */
function useFirefoxDragClickWorkaround(chartRef: React.RefObject<Highcharts.Chart>) {
  const dragging = useRef<boolean>(false);
  const startPosition = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  const handleMouseMove = React.useCallback((e: MouseEvent) => {
    /** @note We using betterZoom MIN_DELTA to match dragging behavior, but only on X axis */
    dragging.current = Math.abs(startPosition.current.x - e.clientX) > MIN_DELTA;
  }, []);

  const handlerChartContainerMouseDown = React.useCallback(
    (e: MouseEvent) => {
      startPosition.current = { x: e.clientX, y: e.clientY };
      Highcharts.addEvent(chartRef.current, "mousemove", handleMouseMove);
    },
    [chartRef, handleMouseMove],
  );

  const handleChartContainerClick = React.useCallback(() => {
    dragging.current = false;
    if (chartRef.current)
      Highcharts.removeEvent(
        chartRef.current,
        "mousemove",
        handleMouseMove as Highcharts.EventCallbackFunction<Highcharts.Chart>,
      );
  }, [chartRef, handleMouseMove]);

  useEffect(() => {
    const container = chartRef.current?.container;
    if (!container) return;
    container.addEventListener("mousedown", handlerChartContainerMouseDown);
    container.addEventListener("click", handleChartContainerClick);
    return () => {
      container.removeEventListener("mousedown", handlerChartContainerMouseDown);
      container.removeEventListener("click", handleChartContainerClick);
    };
  }, [chartRef, handlerChartContainerMouseDown, handleChartContainerClick]);

  return dragging;
}

function useAnalysisMouseSelect({
  projectId,
  chartRef,
  analyses,
}: {
  projectId: string;
  chartRef: React.RefObject<Highcharts.Chart>;
  analyses: Analysis[];
}) {
  const { setBoundary } = useSelectedAnalyses(projectId, analyses);
  const getSelectableAnalysis = React.useCallback(
    (dateNumber: number) => closestSelectableAnalysis(analyses, dateNumber),
    [analyses],
  );

  const dragging = useFirefoxDragClickWorkaround(chartRef);

  const handleChartClick = React.useCallback(
    (e: Highcharts.PointerEventObject) => {
      /** @note DO NOT REMOVE: firefox drag+click fix */
      if (dragging.current) return;

      if (e.altKey || e.ctrlKey || e.metaKey) return;
      const chart = chartRef.current;
      if (!chart) {
        throw new Error("chart not found");
      }
      const xAxis = chart.xAxis[0];
      if (!xAxis) throw new Error("Missing xAxis");
      const clickedDateNumber = xAxis.toValue(e.chartX);
      const closestAnalysis = getSelectableAnalysis(clickedDateNumber);
      if (closestAnalysis === null) return;
      const closestAnalysisDate = Number(closestAnalysis.date);

      const { min, max } = xAxis.getExtremes();
      if (closestAnalysisDate >= min && closestAnalysisDate <= max)
        if (e.shiftKey) {
          setBoundary(oldBoundary => ({ pivot: oldBoundary.pivot, to: closestAnalysisDate }));
        } else {
          setBoundary({ pivot: closestAnalysisDate, to: null });
        }
    },
    [chartRef, dragging, getSelectableAnalysis, setBoundary],
  );
  useChartEventHandler(
    chartRef,
    "click",
    handleChartClick as Highcharts.EventCallbackFunction<Highcharts.Chart>,
  );

  const handlePointClick = React.useCallback(
    (e: Highcharts.PointClickEventObject) => {
      if (e.altKey || e.metaKey || e.ctrlKey || e.shiftKey) return;
      const analysis = getSelectableAnalysis(e.point.x);
      if (analysis === null) return;
      setBoundary({ pivot: analysis.date, to: null });
    },
    [getSelectableAnalysis, setBoundary],
  );
  useChartEventHandler(
    chartRef,
    "pointClick",
    handlePointClick as Highcharts.EventCallbackFunction<Highcharts.Chart>,
  );
}

export default useAnalysisMouseSelect;
